import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getPropertyDetailFull } from "../../legacy/api/propertyDetail";
import { apiGet } from "../../legacy/services/apiClient";
import useBookingStore from "../../legacy/store/bookingStore";
import { useBookingContext } from "../../legacy/context/BookingContext";
import { formatPrice, normalizePropertyCard } from "../mobileUtils";
import { extractElivaasProviderId } from "../../utils/providerRoutes";
import "../styles/mobile-theme.css";
import MobileHeader from "../components/MobileHeader";
import MobilePropertyCard from "../components/MobilePropertyCard";

const toLocalIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildDefaultProviderDateRange = () => {
  const checkInDate = new Date();
  const checkOutDate = new Date(checkInDate);
  checkOutDate.setDate(checkOutDate.getDate() + 1);
  return {
    checkIn: toLocalIsoDate(checkInDate),
    checkOut: toLocalIsoDate(checkOutDate),
  };
};

export default function MobilePropertyDetail() {
  const navigate = useNavigate();
  const { slug, propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const routeIdentifier = propertyId || slug;
  const routeProviderId =
    propertyId ||
    extractElivaasProviderId(routeIdentifier) ||
    (/^prop_[a-z0-9]+$/i.test(String(routeIdentifier || "")) ? routeIdentifier : "");
  const id = routeProviderId || routeIdentifier;
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [photo, setPhoto] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const [saved, setSaved] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [detailData, setDetailData] = useState({});
  const [mealPlan, setMealPlan] = useState("");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [similar, setSimilar] = useState([]);
  const { clearBooking } = useBookingContext();
  const resetBooking = useBookingStore((state) => state.resetBooking);
  const setStayData = useBookingStore((state) => state.setStayData);
  const setSelection = useBookingStore((state) => state.setSelection);
  const setDates = useBookingStore((state) => state.setDates);
  const setGuests = useBookingStore((state) => state.setGuests);

  useEffect(() => {
    let active = true;
    const defaultProviderDateRange = routeProviderId ? buildDefaultProviderDateRange() : { checkIn: "", checkOut: "" };
    const selectedCheckIn = searchParams.get("checkIn") || searchParams.get("checkin") || defaultProviderDateRange.checkIn;
    const selectedCheckOut = searchParams.get("checkOut") || searchParams.get("checkout") || defaultProviderDateRange.checkOut;
    if (id) getPropertyDetailFull(id, {
      providerCode: routeProviderId ? "elivaas" : "",
      checkIn: selectedCheckIn || undefined,
      checkOut: selectedCheckOut || undefined,
      adults: Number(searchParams.get("adults") || searchParams.get("guests") || 2) || 2,
      children: Number(searchParams.get("children") || 0) || 0,
      propertyName: searchParams.get("propertyName") || undefined,
    })
      .then((payload) => {
        if (active) setDetailData(payload || {});
        const detail = payload?.property || payload?.data?.property || payload?.data || payload;
        const source = detail ? {
          ...detail,
          images: detail.images || payload?.gallery?.images || payload?.media?.images || [],
          amenities: detail.amenities || payload?.amenities?.items || [],
          description: detail.description || detail.long_description || payload?.description,
          review_count: detail.review_count || payload?.reviews?.total,
        } : null;
        if (active && source) setProperty(normalizePropertyCard(source));
      })
      .catch(async (err) => {
        if (!active) return;
        try {
          const params = new URLSearchParams({ search: id, limit: "20" });
          if (selectedCheckIn) params.set("checkIn", selectedCheckIn);
          if (selectedCheckOut) params.set("checkOut", selectedCheckOut);
          const payload = await apiGet(`/api/public/properties?${params.toString()}`);
          const rows = payload?.items || payload?.data || payload?.results || [];
          const match = (Array.isArray(rows) ? rows : []).find((item) =>
            String(item.provider_property_id || item.slug || item.id) === String(id)
          );
          if (match && active) {
            setProperty(normalizePropertyCard(match));
            setError("");
            return;
          }
        } catch (_) {
          // The primary provider error below is the useful user-facing message.
        }
        if (active) setError(err?.message || "Property details could not be loaded.");
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [id, routeProviderId, searchParams]);

  useEffect(() => {
    const latitude = property?.raw?.latitude;
    const longitude = property?.raw?.longitude;
    if (!latitude || !longitude || !property?.raw?.id) return;
    let active = true;
    const params = new URLSearchParams({ lat: latitude, lng: longitude, radiusKm: "50", propertyId: property.raw.id });
    apiGet(`/api/properties/nearby?${params}`).then((payload) => {
      const rows = payload?.items || payload?.data || payload || [];
      if (active) setSimilar(Array.isArray(rows) ? rows.slice(0, 6).map(normalizePropertyCard) : []);
    }).catch(() => active && setSimilar([]));
    return () => { active = false; };
  }, [property]);

  const amenities = useMemo(() => property?.amenities || [], [property]);
  const images = property?.images?.length ? property.images : property?.image ? [property.image] : [];
  const raw = property?.raw || {};
  const facts = [
    property?.bedrooms ? ["bi-house-door", `${property.bedrooms} Bedrooms`] : null,
    property?.guests ? ["bi-people", `${property.guests} Guests`] : null,
    property?.bathrooms ? ["bi-droplet", `${property.bathrooms} Baths`] : null,
    raw.beds ? ["bi-moon", `${raw.beds} Beds`] : null,
  ].filter(Boolean);

  const startBooking = () => {
    const providerStayId = routeProviderId ? `provider:elivaas:${routeProviderId}` : "";
    const selectedCheckIn = searchParams.get("checkin") || searchParams.get("checkIn") || searchParams.get("from") || "";
    const selectedCheckOut = searchParams.get("checkout") || searchParams.get("checkOut") || searchParams.get("to") || "";
    const selectedGuests = Number(searchParams.get("guests") || searchParams.get("adults") || 2) || 2;
    const nightlyPrice = Number(
      property.price ||
        raw.price ||
        raw.display_sell_price ||
        raw.final_per_night_display_price ||
        raw.sell_price_per_night ||
        raw.sellPricePerNight ||
        raw.price_per_night ||
        raw.base_price_per_night ||
        raw.price_from ||
        raw.tariff ||
        raw.rate ||
        0
    );
    const stay = {
      ...raw,
      id: providerStayId || raw.id || property.id,
      slug: routeProviderId || raw.slug || id,
      provider_code: routeProviderId ? "elivaas" : raw.provider_code,
      provider_property_id: routeProviderId || raw.provider_property_id,
      title: property.title,
      name: property.title,
      city: raw.city,
      area: raw.area,
      location: property.location,
      coverImage: property.image,
      featured_image: raw.featured_image || property.image,
      image_url: raw.image_url || property.image,
      currency: property.currency || raw.currency || "INR",
      price: nightlyPrice,
      nightly_rate: nightlyPrice,
      base_price: nightlyPrice,
      min_nights: Math.max(1, Number(raw.min_nights || 1)),
      max_guests: raw.max_guests || property.guests,
      guests: property.guests,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      room_types: raw.room_types || [],
      booking_mode: raw.booking_mode || "property",
      addons,
      services: addons,
    };
    clearBooking();
    resetBooking();
    setStayData(stay);
    if (selectedCheckIn && selectedCheckOut) setDates(selectedCheckIn, selectedCheckOut);
    setGuests(selectedGuests);
    setSelection({
      stayId: stay.id,
      checkIn: selectedCheckIn || undefined,
      checkOut: selectedCheckOut || undefined,
      guests: selectedGuests,
      mealPlanId: mealPlan,
      selectedAddons,
      promoCode: selectedCoupon?.code || "",
      appliedCoupon: selectedCoupon,
    });
    navigate("/booking/select-dates");
  };

  const meals = detailData.mealPlans || [];
  const addons = detailData.addons || [];
  const reviews = detailData.reviews?.items || raw.reviews || [];
  const coupons = detailData.pricingContext?.availableCoupons || [];
  const policyTabs = (detailData.aboutHomeTabs || []).filter((tab) => tab.id !== "about-stay" && (tab.content?.text || tab.content?.list?.length || tab.content?.sections?.length));

  const showPrevPhoto = () => {
    if (!images.length) return;
    setPhoto((current) => (current - 1 + images.length) % images.length);
  };

  const showNextPhoto = () => {
    if (!images.length) return;
    setPhoto((current) => (current + 1) % images.length);
  };

  const handleGalleryTouchEnd = (event) => {
    if (touchStartX == null) return;
    const endX = event.changedTouches?.[0]?.clientX;
    setTouchStartX(null);
    if (typeof endX !== "number") return;
    const delta = endX - touchStartX;
    if (Math.abs(delta) < 35) return;
    if (delta < 0) showNextPhoto();
    else showPrevPhoto();
  };

  if (loading) return <div className="hv-mobile-shell hv-ref-detail"><div className="hv-mobile-detail-loading" role="status">Loading property details…</div></div>;
  if (!property) return <div className="hv-mobile-shell hv-ref-detail"><div className="hv-mobile-detail-error" role="alert"><h1>Property unavailable</h1><p>{error || "Please try again."}</p><button onClick={() => navigate(-1)}>Go back</button></div></div>;

  return <div className="hv-mobile-shell hv-ref-detail">
    <MobileHeader showBack backTo="/stays" />
    <nav className="hv-ref-rail" aria-label="Booking progress"><div className="hv-ref-track" />{["Stay", "Guests", "Review", "Payment"].map((step, index) => <div className={`hv-ref-step ${index === 0 ? "active" : ""}`} key={step}><span>{index + 1}</span><b>{step}</b></div>)}</nav>
    <main className="hv-ref-shell">
    <div className="hv-ref-crumb">Home › {property.title}</div>
    <article className="hv-ref-detailcard">
    <div className="hv-detail-hero">
      <div
        className="hv-detail-gallery"
        style={{ transform: `translateX(-${photo * 100}%)` }}
        onClick={() => setGalleryOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") setGalleryOpen(true); }}
        aria-label="Open full screen photo gallery"
      >
        {images.map((image, index) => <img key={`${image}-${index}`} src={image} onError={(event) => { event.currentTarget.style.visibility = "hidden"; }} alt={`${property.title} - ${index + 1}`} loading={index ? "lazy" : "eager"} />)}
      </div>
      {(raw.badge || raw.badges?.[0] || raw.property_type) ? <span className="hv-ref-badge">{raw.badge || raw.badges?.[0] || raw.property_type}</span> : null}
      <div className="hv-floating-actions">
        <button className="hv-icon-btn" type="button" onClick={() => navigate(-1)} aria-label="Back"><i className="bi bi-chevron-left" /></button>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="hv-icon-btn" type="button" aria-pressed={saved} onClick={() => setSaved((value) => !value)} aria-label="Save"><i className={`bi ${saved ? "bi-heart-fill" : "bi-heart"}`} /></button>
          <button className="hv-icon-btn" type="button" onClick={() => navigator.share?.({ title: property.title, url: window.location.href })} aria-label="Share"><i className="bi bi-share" /></button>
        </div>
      </div>
      {images.length > 1 && <><button className="hv-gallery-arrow left" onClick={(event) => { event.stopPropagation(); showPrevPhoto(); }} aria-label="Previous photo">‹</button><button className="hv-gallery-arrow right" onClick={(event) => { event.stopPropagation(); showNextPhoto(); }} aria-label="Next photo">›</button><div className="hv-ref-dots">{images.map((_, index) => <button key={index} className={index === photo ? "active" : ""} onClick={(event) => { event.stopPropagation(); setPhoto(index); }} aria-label={`Photo ${index + 1}`} />)}</div></>}
      <span className="hv-image-count">{photo + 1}/{images.length}</span>
    </div>
    <div className="hv-ref-titleblock">
      {error && <div className="hv-card hv-detail-error">{error}</div>}
      <div className="hv-ref-eyebrow">{property.location}</div>
      <h1>{property.title}</h1>
      {raw.short_description && <p className="hv-ref-tagline">{raw.short_description}</p>}
      <div className="hv-ref-rating"><span>★ {property.rating ? Number(property.rating).toFixed(1) : "New"}</span> · {raw.review_count || raw.reviews_count || 0} verified reviews · <b>Rare find</b></div>
      <div className="hv-detail-facts">{facts.map(([icon, label]) => <div key={label}><i className={`bi ${icon}`} /><span>{label}</span></div>)}</div>
    </div>{(raw.host?.name || raw.host_name) ? <div className="hv-ref-host"><div className="hv-ref-avatar">{String(raw.host?.name || raw.host_name)[0]}</div><div><strong>Hosted by {raw.host?.name || raw.host_name}</strong>{raw.host?.response_time?<small>{raw.host.response_time}</small>:null}{raw.host?.is_superhost?<em>◆ Superhost</em>:null}</div></div> : null}</article>
    <nav className="hv-ref-tabs" aria-label="Property sections">
      <a href="#overview">Overview</a>
      {meals.length ? <a href="#meal-plans">Meal plans</a> : null}
      {property.description ? <a href="#about-home">About home</a> : null}
      {amenities.length ? <a href="#amenities">Amenities</a> : null}
      {addons.length ? <a href="#services">Services</a> : null}
      {raw.latitude && raw.longitude ? <a href="#location">Location</a> : null}
      {property.rating || reviews.length ? <a href="#reviews">Reviews</a> : null}
    </nav>
    {meals.length ? <section id="meal-plans" className="hv-ref-card"><div className="hv-ref-eyebrow">Meal plans</div><h2>Dine your way</h2><div className="hv-ref-options">{meals.map((meal,index) => {const mealId=String(meal.id || meal.rate_plan_code || index);const amount=Number(meal.amount ?? meal.price ?? meal.total ?? 0);return <button className={mealPlan===mealId || (!mealPlan && index===0)?"selected":""} key={mealId} onClick={()=>setMealPlan(mealId)}><span aria-hidden="true">◈</span><div><strong>{meal.name || meal.title}</strong><small>{meal.description}</small></div><b>{amount?formatPrice(amount,property.currency):"Included"}</b></button>})}</div></section> : null}
    {property.description ? <section id="about-home" className="hv-ref-card"><div className="hv-ref-eyebrow">About this home</div><h2>About your stay</h2><p className={`hv-ref-copy ${expanded ? "expanded" : ""}`}>{property.description}</p>{property.description.length > 180 && <button className="hv-ref-more" onClick={() => setExpanded(!expanded)}>{expanded ? "Show less" : "Read the full story"}</button>}</section> : null}
    {amenities.length ? <section id="amenities" className="hv-ref-card"><div className="hv-ref-eyebrow">Villa amenities & features</div><h2>Everything on the estate</h2><div className="hv-ref-amens">{amenities.slice(0, 10).map((item) => <span key={String(item?.id || item?.name || item)}><i className="bi bi-check2" />{String(item?.name || item)}</span>)}</div></section> : null}
    {policyTabs.length ? <section className="hv-ref-card"><div className="hv-ref-eyebrow">Policies & house rules</div><h2>Good to know</h2><div className="hv-ref-policies">{policyTabs.map((tab)=><details key={tab.id}><summary>{tab.label}</summary>{tab.content?.text?<p>{tab.content.text}</p>:null}{tab.content?.list?.length?<ul>{tab.content.list.map((item,index)=><li key={index}>{typeof item === "string" ? item : item.text || item.label}</li>)}</ul>:null}</details>)}</div></section> : null}
    {addons.length ? <section id="services" className="hv-ref-card"><div className="hv-ref-eyebrow">Customise your stay</div><h2>Premium services</h2><p className="hv-ref-sub">Added services carry into your folio at checkout.</p><div className="hv-ref-addons">{addons.map((addon,index)=>{const aid=String(addon.id||index);const on=selectedAddons.some(item=>String(item.id||item)===aid);return <div className={on?"selected":""} key={aid}><span aria-hidden="true">✦</span><section><strong>{addon.name||addon.title}</strong>{(addon.description||addon.short_description)?<small>{addon.description||addon.short_description}</small>:null}<b>{formatPrice(addon.amount??addon.price,property.currency)} <i>/ stay</i></b></section><button onClick={()=>setSelectedAddons(on?selectedAddons.filter(item=>String(item.id||item)!==aid):[...selectedAddons,{...addon,id:aid}])}>{on?"✓ Added":"+ Add"}</button></div>})}</div></section> : null}
    {coupons.length ? <section className="hv-ref-card"><div className="hv-ref-eyebrow">Available offers</div><h2>Save on your stay</h2><div className="hv-ref-coupons">{coupons.map((coupon,index)=>{const code=String(coupon.code||coupon.coupon_code||coupon.id||index);const active=selectedCoupon&&String(selectedCoupon.code||selectedCoupon.coupon_code||selectedCoupon.id)===code;return <div key={code}><section><strong>{coupon.code||coupon.coupon_code}</strong>{coupon.description?<small>{coupon.description}</small>:null}</section><button type="button" onClick={()=>setSelectedCoupon(active?null:{...coupon,code:coupon.code||coupon.coupon_code})}>{active?"Remove":"Apply"}</button></div>})}</div><p className="hv-ref-sub">Eligibility and final discount are verified by the booking server.</p></section> : null}
    {raw.latitude && raw.longitude ? <section id="location" className="hv-ref-card"><div className="hv-ref-eyebrow">Location</div><h2>{property.location}</h2><div className="hv-ref-map"><span>⌖</span><b>{property.location}</b></div>{detailData.nearbyPoints?.length?<div className="hv-ref-pills">{detailData.nearbyPoints.slice(0,5).map((point,index)=><span key={point.id||index}>{point.name}{point.distance?` · ${point.distance}`:""}</span>)}</div>:null}<p className="hv-ref-sub">Exact location is shared after booking confirmation.</p></section> : null}
    {property.rating || reviews.length ? <section id="reviews" className="hv-ref-card"><div className="hv-ref-eyebrow">Verified guest reviews</div><h2>What guests say</h2><div className="hv-ref-review-summary"><strong>{Number(property.rating||0).toFixed(1)}</strong><span>★★★★★<small>{raw.review_count||reviews.length} reviews</small></span></div><div className="hv-ref-reviews">{reviews.slice(0,4).map((review,index)=><article key={review.id||index}><span>{String(review.name||review.guest_name||"G")[0]}</span><div><strong>{review.name||review.guest_name||"Verified guest"}</strong><small>✓ Verified stay</small><p>{review.text||review.comment||review.review}</p></div><b>{"★".repeat(Math.max(0,Math.min(5,Number(review.rating||review.score||5))))}</b></article>)}</div></section> : null}
    {similar.length ? <section className="hv-ref-card"><div className="hv-ref-eyebrow">Keep exploring</div><h2>More Homevilas stays</h2><div className="hv-ref-similar-live">{similar.map((item)=><MobilePropertyCard key={item.id} property={item} horizontal />)}</div></section> : null}
    <section className="hv-ref-final hv-ref-final-inline"><span>From</span><strong>{formatPrice(property.price,property.currency)} <small>{property.priceSubtitle || "/ night"} · taxes extra</small></strong><button onClick={startBooking}>Book your stay →</button></section>
    </main>
    {galleryOpen ? <div className="hv-ref-gallery-modal" role="dialog" aria-modal="true" aria-label={`${property.title} photo gallery`}>
      <button className="hv-ref-gallery-close" type="button" onClick={() => setGalleryOpen(false)} aria-label="Close gallery">×</button>
      <div className="hv-ref-gallery-title"><strong>{property.title}</strong><span>{photo + 1} / {images.length}</span></div>
      <div
        className="hv-ref-gallery-stage"
        onTouchStart={(event) => setTouchStartX(event.touches?.[0]?.clientX ?? null)}
        onTouchEnd={handleGalleryTouchEnd}
      >
        {images.length > 1 ? <button className="hv-ref-gallery-nav left" type="button" onClick={showPrevPhoto} aria-label="Previous image">‹</button> : null}
        <img src={images[photo]} alt={`${property.title} - ${photo + 1}`} />
        {images.length > 1 ? <button className="hv-ref-gallery-nav right" type="button" onClick={showNextPhoto} aria-label="Next image">›</button> : null}
      </div>
    </div> : null}
    <div className="hv-ref-bar"><button className="hv-ref-total" onClick={startBooking}><span>{property.priceSubtitle || "From · per night"}</span><strong>{formatPrice(property.price, property.currency)}</strong></button><button className="hv-ref-book" onClick={startBooking}>Book Now →</button></div>
  </div>;
}
